import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const QUALS = ["FA Playmaker", "Introduction to Coaching Football (Level 1)", "UEFA C (Level 2)", "UEFA B", "UEFA A", "Other"];
const CERTS = [["dbs", "DBS"], ["safeguarding_children", "Safeguarding Children"], ["intro_first_aid", "Introduction to First Aid"]];
const UNCHANGED = "__keep__";

// Apply the same qualification / certificate values to many coaches at once.
export function BulkQualificationDialog({ open, onClose, coachIds, onDone }) {
  const [qual, setQual] = useState(UNCHANGED);
  const [customQual, setCustomQual] = useState("");
  const [certs, setCerts] = useState({});
  const [busy, setBusy] = useState(false);

  const setCert = (k, patch) => setCerts(c => ({ ...c, [k]: { ...(c[k] || { held: UNCHANGED, expiry: "" }), ...patch } }));
  const build = () => {
    const ch = {};
    if (qual !== UNCHANGED) ch.highest_coaching_qualification = qual === "Other" ? customQual.trim() : qual;
    for (const [k] of CERTS) {
      const c = certs[k]; if (!c) continue;
      if (c.held !== UNCHANGED) ch[`${k}_held`] = c.held === "yes";
      if (c.expiry) ch[`${k}_expiry`] = c.expiry;
    }
    return ch;
  };
  const changes = build();
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coaches/bulk-profile", { user_ids: coachIds, changes });
      toast.success(`Updated ${data.updated} coach${data.updated === 1 ? "" : "es"}`); onDone?.(); onClose?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk update failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="bulk-qual-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">Bulk update {coachIds.length} coach{coachIds.length === 1 ? "" : "es"}</DialogTitle>
          <DialogDescription className="text-xs opacity-70">Only the fields you set here change — everything left as "Don't change" is kept as-is for each coach.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Highest FA coaching qualification</Label>
            <Select value={qual} onValueChange={setQual}>
              <SelectTrigger data-testid="bulk-qual-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={UNCHANGED}>Don't change</SelectItem>{QUALS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
            </Select>
            {qual === "Other" && <Input value={customQual} onChange={e => setCustomQual(e.target.value)} placeholder="Type the qualification" data-testid="bulk-qual-custom" className="mt-2" />}
          </div>
          {CERTS.map(([k, label]) => {
            const c = certs[k] || { held: UNCHANGED, expiry: "" };
            return (
              <div key={k} className="grid grid-cols-2 gap-2 items-end" data-testid={`bulk-cert-${k}`}>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">{label}</Label>
                  <Select value={c.held} onValueChange={v => setCert(k, { held: v })}>
                    <SelectTrigger data-testid={`bulk-cert-${k}-held`} className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value={UNCHANGED}>Don't change</SelectItem><SelectItem value="yes">Held</SelectItem><SelectItem value="no">Not held</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Expiry (optional)</Label>
                  <Input type="date" value={c.expiry} onChange={e => setCert(k, { expiry: e.target.value })} data-testid={`bulk-cert-${k}-expiry`} className="mt-1.5" />
                </div>
              </div>
            );
          })}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase">Cancel</button>
            <button type="button" onClick={apply} disabled={busy || Object.keys(changes).length === 0 || (qual === "Other" && !customQual.trim())} data-testid="bulk-qual-apply" className="btn-volt px-5 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">{busy ? "Applying…" : `Apply ${Object.keys(changes).length} change${Object.keys(changes).length === 1 ? "" : "s"}`}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
